import { go2LoginPage } from '~/utils/auth'
import { useMySystemStore } from './../stores/system'
import { useSessionStorage, useStorage } from '@vueuse/core'

// 标识位
const confirm = false
let isRefreshing = false
let refreshSubscribers: Array<(newToken: string) => void> = []

// 上下文获取策略
function getContext() {
  return {
    prjId: useSessionStorage('prjId', ''),
    clientId: useStorage('clientId', ''),
    systemStore: useMySystemStore(),
  }
}

export const useUseRequest = () => {
  function handleRefreshToken(options: any) {
    const refreshTokenStr = getRefreshToken() || ''
    const expiresTimeIn = parseInt(getExpiresTimeIn(), 10)
    // 提前五分钟获取 token
    const distance = 60 * 5 * 1000
    if (
      refreshTokenStr &&
      expiresTimeIn - Date.now() <= distance &&
      expiresTimeIn - Date.now() > 0
    ) {
      if (!isRefreshing) {
        isRefreshing = true
        fetch('/auth/refreshToken', {
          method: 'POST',
          body: {
            refreshToken: refreshTokenStr,
          },
        })
          .then((res: any) => {
            if (res.code === 0) {
              const { accessToken, expireIn, refreshToken } = res.data
              const newTimestamp = Date.now() + expireIn * 1000
              setToken(accessToken)
              setExpiresIn(expireIn)
              setRefreshToken(refreshToken)
              setExpiresTimeIn(newTimestamp)
              refreshSubscribers.forEach((cb) => cb(accessToken))
              refreshSubscribers = []
            } else {
              console.error('刷新 token 失败')
            }
          })
          .catch((err) => {})
          .finally(() => {
            isRefreshing = false
          })

        return new Promise((resolve) => {
          refreshSubscribers.push((newToken: string) => {
            options.headers.Authorization = `Bearer ${newToken}`
            resolve(options)
          })
        })
      }
    }
  }

  function handleRequest(options: any, headers = {}) {
    const { systemStore, prjId, clientId } = getContext()
    options.headers = headers
    options.headers['Accept-Language'] = systemStore.language
    // 复杂的请求头 会触发预检导致GET 请求失败 这里做逻辑区分
    if (options.method === 'POST') {
      options.headers['APP-ID'] = systemStore.appId
      options.headers['terminal-type'] = systemStore.terminalType
      if (isDev()) {
        options.headers['terminal-type'] = 'PC'
      }
      options.headers['Content-Type'] = 'application/json'
      options.headers['Access-Control-Allow-Origin'] = '*'

      if (!_isEmpty(prjId.value)) {
        options.headers['popular-channel'] = _isNumber(prjId.value)
          ? prjId.value
          : prjId.value.indexOf(',') > 0
            ? prjId.value.split(',')[0]
            : prjId.value
      }

      if (!_isEmpty(clientId.value)) {
        options.headers['client-uid'] = clientId.value
      }

      if (systemStore.targetPageCode && !options.headers['page-code']) {
        options.headers['page-code'] = systemStore.targetPageCode
      }
    }

    const token = getToken()
    if (token) {
      options.headers['Authorization'] = `Bearer ${token}`
    }

    handleRefreshToken(options)
  }

  function handleResponse(response: any) {
    const { systemStore } = getContext()
    const nuxtApp = useNuxtApp()
    let path = '/'
    if (nuxtApp) {
      const route = useRoute()
      path = route.path
    }
    try {
      if (response._data.code === 401) {
        if (
          response &&
          response._data &&
          response._data.subcode === 'TOKEN_HAS_BEEN_REJECTED'
        ) {
          if (!confirm) {
            systemStore.logout().then(() => {
              go2LoginPage(path)
            })
          } else {
            systemStore.logout()
          }
        } else {
          systemStore.logout().then(() => {
            go2LoginPage(path)
          })
        }
      }
    } catch (error) {
      console.error('处理响应时发生错误:', error)
    }
  }

  function fetch<T>(url: string, opt: any, apiOptions: any = {}) {
    const systemStore = useMySystemStore()
    const options = opt
    options.lazy = options?.lazy ?? true

    const ccFetch = $fetch.create({
      timeout: apiOptions?.timeout ?? 15000,
      baseURL: apiOptions?.baseApi ?? systemStore.proxyApi ?? '/api',
      onRequest: ({ request, options, error }) => {
        handleRequest(options, apiOptions?.headers)
      },
      onRequestError({ request, options, error }) {
        return Promise.reject(error)
      },
      onResponse({ response, options, error }) {
        handleResponse(response)
      },
      onResponseError({ response, options, error }) {
        console.error('==============================', error)

        if (response && response._data) {
          const { code, describe } = response._data
          return Promise.reject({
            message: describe || '请求失败',
            code: code || 500,
            data: response._data,
          })
        }
        return Promise.reject({
          message: error?.message || '发生未知错误',
          code: 500,
        })
      },
    })
    return ccFetch(url, options)
  }

  const get = <T>(url: string, params = {}) => {
    return fetch<T>(url, {
      method: 'GET',
      params,
    })
  }

  const getAsset = <T>(url: string, params = {}) => {
    return fetch<T>(
      url,
      {
        method: 'GET',
        params,
      },
      {
        baseApi: 'https://sapi.jctrans.com',
      },
    )
  }

  const post = <T>(url: string, body = {}, params = {}, options = {}) => {
    return fetch<T>(
      url,
      {
        method: 'POST',
        body,
        params,
      },
      options,
    )
  }

  return {
    get,
    post,
    getAsset,
    fetch,
  }
}
